import Joi from "joi";
import {logger} from "../services/LogService";

export const VALIDATION_MESSAGE_TELEFON = "Zahlen 0 – 9, als Trennzeichen nur „-„ zulässig";

const realExtension = Joi.extend({
    type: "string",
    base: Joi.string(),
    coerce: {
        from: "number",
        method(value) {
            if (!isNaN(parseFloat(value)) && isFinite(value)) {
                return {value: value.toString()};
            }
            return {value};
        },
    },
});

/**
 * Funktion, um ein Item gegen ein schema zu validieren.
 * @param item das zu validierende Objekt
 * @param schema das Schema, gegen welches validiert werden soll
 * @param options optionen können überschrieben werden. Default ist { abortEarly: false }
 * @returns {{}|null}
 */
function validate(item, schema, options = {abortEarly: false}) {
    const newErrors = {};

    const {error} = Joi.object(schema).validate(item, options);
    if (!error) return null;

    for (let err of error.details) {
        newErrors[err.path[0]] = err.message;
    }
    return newErrors;
}

/**
 * Fügt Validatoren hinzu, welche prüfen, dass es sich um einen notwendigen String handelt
 * @returns {*}
 */
function requiredString() {
    return required(Joi.string());
}

/**
 * Fügt Validatoren hinzu, welche prüfen, dass ein gültiges Datumsformat vorhanden ist
 * @returns {*}
 */
function requiredDateFormat() {
    return required(Joi.string().pattern(/^([TMJ]\.[TMJ]|T|M|J)+$/).message("Ungültiges Datumsformat"));
}

/**
 * Fügt Validatoren hinzu, welche prüfen, dass es sich um ein notwendiges Date handelt
 * (ZUR NUTZUNG MIT DEM FORMUTILS-DATEPICKER)
 * @returns {*}
 */
function requiredDate() {
    return required(Joi.date());
}

/**
 * Fuegt allow(null) und allow("") an ein Joi objekt an
 * @returns {*}
 */
function nullable(joi) {
    return joi.allow(null).allow("");
}

/**
 * Liefert einen nullable Joi-String-Objekt zurueck.
 * @returns {*}
 */
function nullableString() {
    return nullable(Joi.string());
}

/**
 * Liefert einen nullable Joi-Number-Objekt zurueck.
 * @returns {*}
 */
function nullableNumber() {
    return nullable(Joi.number()).error((errors) => {
        errors[0].message = "Es muss eine Zahl oder nichts eingegeben werden.";
        return errors[0];
    });
}

function optionalArray() {
    return Joi.array().optional();
}

function optionalBool() {
    return Joi.bool().optional();
}

/**
 * Fügt Validatoren hinzu, welche prüfen, dass es sich um einen notwendiges Objekt handelt
 * @returns {*}
 */
function requiredObject(obj) {
    return required(Joi.object(obj));
}

/**
 * Fügt Validatoren hinzu, welche prüfen, dass es sich um einen notwendiges Wertelisten Objekt handelt
 * @returns {*}
 */
function requiredWerteListeObjekt() {
    return requiredObject({
        id: required(Joi.alternatives().try(Joi.number(), Joi.string())),
        value: requiredString(),
    });
}

/**
 * Fügt Validatoren hinzu, welche prüfen, dass es sich um einen notwendiges Enum Wertelisten Objekt handelt
 * @returns {*}
 */
function requiredEnumWerteListeObjekt() {
    return requiredObject({
        id: requiredString(),
        value: requiredString(),
    });
}

function isReal({requiredField = true, positive = true}) {
    let validator = realExtension
        .string().allow(null, '')
        .pattern(/^-?(0*\d{1,10})?(\.?|\.(\d{1,3}))$/)
        .message("Der Wert darf maximal 10 Stellen vor und drei Stellen nach dem Komma haben!");
    if (!requiredField) {
        validator = validator.allow(null, '').optional();
    }
    if (positive) {
        validator = validator
            .pattern(/^[^-]/)
            .message("Es muss ein positiver Wert angegeben werden!")
            .invalid("0", "0.", ".", "0.0", "0.00", "")
            .messages({"any.invalid": "Es muss ein positiver Wert angegeben werden!"});
    }
    return requiredField ? required(validator) : validator;
}

/**
 * Fügt Validatoren hinzu, welche prüfen, dass es sich um eine notwendige Number handelt
 * @returns {*}
 */
function requiredNumber() {
    return required(Joi.number());
}

/**
 * Prüft, ob die Eingabe dem Regulären Ausdruck entspricht
 * @param regEx
 * @param isRequired Gibt an, ob das Feld required ist
 * @param message Die anzuzeigende Nachricht, falls die Eingabe das Pattern nicht erfüllt
 * @returns {Joi.StringSchema}
 */
function regex(regEx, isRequired, message) {
    const pattern = Joi.string().pattern(regEx).message(message);
    return isRequired ? required(pattern) : nullable(pattern);
}

function required(joi) {
    return joi.required().messages({
        "any.required": buildRequiredMessage(),
        "date.base": buildRequiredMessage(),
        "number.base": buildRequiredMessage(),
        "object.base": buildRequiredMessage(),
        "string.regex.base": buildRequiredMessage(),
        "string.empty": buildRequiredMessage(),
        "string.base": buildRequiredMessage(),
        "any.empty": buildRequiredMessage(),
    });
}

function buildRequiredMessage() {
    //Sorgt dafür, dass ein leeres <span/> im HelperText auftaucht, welches per CSS ausgeblendet werden kann (EAB-330)
    return " ";
}

/**
 * Prüft, ob die E-Mail vorhanden ist und dem korrekten Pattern entspricht
 * @param label
 * @returns {*}
 */
function validEmail(label) {
    return Joi.string()
        .required()
        .email({tlds: {allow: false}})
        .messages({
            "any.required": buildRequiredMessage(),
            "string.empty": buildRequiredMessage(),
            "string.email": "Das Feld muss eine gültige E-Mail Adresse enthalten!",
        });
}

function maxNumber(maxValue) {
    return Joi.number().integer().max(maxValue).allow('')
        .messages({
            'number.max': `Wert darf nicht größer als ${maxValue} sein.`,
        });
}

/**
 * Wirft die joi-expression einen Fehler, wird die angegebene message als Text angezeigt.
 * @param joi
 * @param message
 * @returns {*}
 */
function errorMessage(joi, message) {
    return joi.error((errors) => {
        logger.debug(errors);
        errors[0].message = message;
        return errors[0];
    });
}

export {
    validate,
    required,
    regex,
    requiredString,
    requiredDateFormat,
    requiredObject,
    requiredWerteListeObjekt,
    requiredEnumWerteListeObjekt,
    requiredNumber,
    requiredDate,
    validEmail,
    errorMessage,
    isReal,
    nullable,
    nullableString,
    nullableNumber,
    optionalArray,
    optionalBool,
    buildRequiredMessage,
    maxNumber
};
